import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, Shield, Menu, X } from "lucide-react";
import { useState } from "react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-[#F0E0CC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 grid grid-cols-3 items-center">
        {/* Left: mobile menu toggle */}
        <div className="flex items-center justify-start">
          <button
            className="md:hidden p-2 -ml-2 text-ink"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Center: brand */}
        <Link
          to="/"
          className="flex items-center justify-center"
          data-testid="nav-home-link"
        >
          <span className="font-heading font-extrabold tracking-tight text-2xl sm:text-3xl text-sage leading-none">
            épicentre
          </span>
        </Link>

        {/* Right: nav (desktop) */}
        <nav className="hidden md:flex items-center justify-end gap-1.5">
          {user?.role === "partner" && (
            <Link
              to="/partner"
              className="px-3 py-2 text-sm text-ink hover:text-sage font-medium inline-flex items-center gap-1.5"
              data-testid="nav-partner-link"
            >
              <LayoutDashboard className="w-4 h-4" /> Mon espace
            </Link>
          )}
          {user?.role === "admin" && (
            <Link
              to="/admin"
              className="px-3 py-2 text-sm text-ink hover:text-sage font-medium inline-flex items-center gap-1.5"
              data-testid="nav-admin-link"
            >
              <Shield className="w-4 h-4" /> Admin
            </Link>
          )}
          {user ? (
            <Button
              onClick={handleLogout}
              variant="outline"
              className="rounded-xl border-[#F0E0CC]"
              data-testid="nav-logout-button"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Déconnexion
            </Button>
          ) : (
            <>
              <Link to="/login">
                <Button
                  variant="outline"
                  className="rounded-xl border-[#F0E0CC]"
                  data-testid="nav-login-button"
                >
                  Connexion
                </Button>
              </Link>
              <Link to="/register">
                <Button
                  className="rounded-xl bg-sage hover:bg-sage-dark text-white"
                  data-testid="nav-register-button"
                >
                  Devenir partenaire
                </Button>
              </Link>
            </>
          )}
        </nav>

        <div className="md:hidden" />
      </div>

      {open && (
        <div className="md:hidden border-t border-[#F0E0CC] bg-white">
          <div className="px-4 py-4 space-y-2">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="block py-2 text-ink font-medium"
              data-testid="nav-events-link-mobile"
            >
              Événements
            </Link>
            {user?.role === "partner" && (
              <Link
                to="/partner"
                onClick={() => setOpen(false)}
                className="block py-2 text-ink font-medium"
                data-testid="nav-partner-link-mobile"
              >
                Mon espace
              </Link>
            )}
            {user?.role === "admin" && (
              <Link
                to="/admin"
                onClick={() => setOpen(false)}
                className="block py-2 text-ink font-medium"
                data-testid="nav-admin-link-mobile"
              >
                Administration
              </Link>
            )}
            {user ? (
              <button
                onClick={handleLogout}
                className="w-full text-left py-2 text-ink font-medium"
                data-testid="nav-logout-button-mobile"
              >
                Déconnexion
              </button>
            ) : (
              <div className="pt-2 flex flex-col gap-2">
                <Link to="/login" onClick={() => setOpen(false)}>
                  <Button variant="outline" className="w-full rounded-xl border-[#F0E0CC]">
                    Connexion
                  </Button>
                </Link>
                <Link to="/register" onClick={() => setOpen(false)}>
                  <Button className="w-full rounded-xl bg-sage hover:bg-sage-dark text-white">
                    Devenir partenaire
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
